import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import { STATE_KEYS } from "utils/constants/state";
import {
  BUTTON_CLASS_CONTEXT,
  BUTTON_TAGS,
  secondaryBackButton,
  SMALL_ICON_SIZE,
  UI_THEME
} from "utils/constants/ui";
import MenuNavHeader from "components/MenuNavHeader";
import HoverIconButton from "components/HoverIconButton";
import DebounceTextarea from "components/DebounceTextarea";
import FieldPicker from "components/FormerEditor/FieldPicker";
import DraggableFieldItem from "./DraggableFieldItem";
import FieldShow from "../FieldShow";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  addModel,
  listDragProps,
  LIST_DESTINATIONS,
  reorderModelsCollection
} from "utils/drag";
import { usePageBuilderContext } from "utils/context";
import Analytics, { DRAG_FIELD_GROUP, SHOW_PRODUCT } from "utils/analytics";
import SlimCTA from "components/SlimCTA";
import { FIELD_GROUP_INTENT } from "utils/constants/fields";
import {
  useFirstComboFromFields,
  useIsManifestEditMode
} from "utils/hooks/manifest";
import {
  FIELD_GROUP_REMOVAL,
  getFieldGroupRemovalSlides
} from "utils/constants/confirm";
import { removeManifestFormFieldGroup } from "utils/manifest";
import ConfirmIconButton from "components/ConfirmIconButton";
import { getSchemaValuesForEditor } from "utils/schema";

const FieldGroupShow = (props) => {
  const name = props.name;
  const schema = props.schema;

  const {
    values,
    touched,
    setValues,
    setTouched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const propertyKey = "fieldGroups";
  const { template } = getSchemaValuesForEditor({
    name,
    propertyKey,
    schema
  });

  const intent = get(values, STATE_KEYS.EDITOR.MENU_SUB_INTENT);
  const setIntent = (intent) =>
    setFieldValue(STATE_KEYS.EDITOR.MENU_SUB_INTENT, intent);

  const isEditMode = useIsManifestEditMode();

  const addFieldIntent = intent === FIELD_GROUP_INTENT.ADD_FIELD;
  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const fieldGroups = get(values, fieldGroupsPath);
  const activeIndex = get(values, STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX);
  const activeSubIndex = get(values, STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX);
  const activeFieldGroup = fieldGroups[activeIndex];

  const pageContext = usePageBuilderContext();
  const tracking = pageContext ? pageContext.tracking : {};

  const fieldGroupPath = `${fieldGroupsPath}[${activeIndex}]`;

  const fieldGroupGoBack = () => {
    setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX, null);
  };

  const fieldGoBack = () => {
    setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX, null);
  };
  const fieldHeaderButtonProps = secondaryBackButton({
    onClick: () => fieldGoBack()
  });

  const titlePath = getFieldPath(fieldGroupPath, "title");
  const title = get(values, titlePath, "");

  const subtitlePath = getFieldPath(fieldGroupPath, "subtitle");
  const subtitle = get(values, subtitlePath, "");

  const fieldsPath = getFieldPath(fieldGroupPath, "fields");
  const fields = get(values, fieldsPath) || [];
  const combo = useFirstComboFromFields(fields);

  const activeFieldPath = `${fieldsPath}[${activeSubIndex}]`;
  const activeField = get(values, activeFieldPath, "");

  const uuidPath = getFieldPath(fieldGroupPath, "uuid");
  const fieldGroupUUID = get(values, uuidPath, "");

  /**
   * {
   *  title: 'Checkout',
   *  subtitle: 'Select your plan to get',
   *  fields: []
   * }
   */
  const hasModels = fields.length > 0;
  const fieldNodes = fields.map((field, fieldIndex) => {
    const itemProps = {
      name: fieldsPath,
      uid: field.uuid,
      title: field.label,
      subtitle: field.subtitle,
      images: field.images,
      index: fieldIndex,
      type: field.uid,
      canRemove: true && !isEditMode,
      canAdd: false && !isEditMode,
      dragDisabled: isEditMode,
      onClick: () => {
        Analytics.track(SHOW_PRODUCT, {
          ...tracking,
          field: field.uuid
        });
        setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX, fieldIndex);
      }
    };

    return (
      <DraggableFieldItem key={`${field.uuid}-${fieldIndex}`} {...itemProps} />
    );
  });

  let content;
  if (addFieldIntent) {
    content = (
      <Fragment>
        <MenuNavHeader />
        <FieldPicker
          onClick={(pickedTemplate) => {
            addModel({
              values,
              setValues,
              setFieldTouched,
              template: pickedTemplate,
              modelsPath: fieldsPath,
              stateKey: STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX
            })();
            setIntent(null);
          }}
        />
      </Fragment>
    );
  } else if (activeField) {
    content = <FieldShow key={activeField.uuid} name={activeFieldPath} />;
  } else if (activeFieldGroup) {
    const actions = (
      <div className="flex flex-row">
        <div className="pr2 mr2 dib br hairline-1">
          <ConfirmIconButton
            icon="delete"
            tooltip={FIELD_GROUP_REMOVAL[0] && FIELD_GROUP_REMOVAL[0].title}
            slides={getFieldGroupRemovalSlides(combo)}
            size={SMALL_ICON_SIZE}
            onConfirm={() => {
              removeManifestFormFieldGroup({
                values,
                touched,
                setValues,
                setTouched,
                name: fieldGroupPath
              });
            }}
          />
        </div>
        <HoverIconButton
          size={SMALL_ICON_SIZE}
          type="add"
          tooltip="Add field group"
          onClick={addModel({
            values,
            setValues,
            setFieldTouched,
            template,
            modelsPath: fieldGroupsPath,
            stateKey: STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX
          })}
        />
      </div>
    );

    const addFieldAction = (
      <div
        className="f6 fw6 link__decoration--hover color-primary pointer"
        onClick={() => {
          setIntent(FIELD_GROUP_INTENT.ADD_FIELD);
        }}
      >
        Add field
      </div>
    );
    const ctxTitle = title || `Group ${activeIndex + 1}`;

    content = (
      <Fragment>
        <MenuNavHeader label={ctxTitle}>{actions}</MenuNavHeader>
        <div className="w-100 ph3 pt3">
          <label className="f6 mb2 dib fw6">Title</label>
          <DebounceTextarea
            uid={fieldGroupUUID}
            inputValue={title}
            placeholder="Optional title"
            debounce={500}
            disabled={isEditMode}
            minRows={1}
            name={titlePath}
            valueSet={(val) => {
              setFieldValue(titlePath, val);
              setFieldTouched(titlePath, true);
            }}
          />
        </div>
        <div className="w-100 ph3 pt3">
          <label className="f6 mb2 dib fw6">Description</label>
          <DebounceTextarea
            uid={fieldGroupUUID}
            inputValue={subtitle}
            placeholder="Optional description"
            debounce={500}
            disabled={isEditMode}
            minRows={1}
            name={subtitlePath}
            valueSet={(val) => {
              setFieldValue(subtitlePath, val);
              setFieldTouched(subtitlePath, true);
            }}
          />
        </div>
        <div className="flex flex-row justify-between items-center ph3 pt3 pb2">
          <div className="fw6 f6">Fields</div>
          {hasModels && addFieldAction}
        </div>
        {hasModels ? (
          <DragDropContext
            onDragEnd={reorderModelsCollection({
              setFieldValue,
              setFieldTouched,
              models: fields,
              modelsPath: fieldsPath,
              tracking: {
                event: DRAG_FIELD_GROUP,
                data: {
                  ...tracking
                }
              }
            })}
          >
            <Droppable droppableId={LIST_DESTINATIONS.FORM}>
              {(provided, snapshot) => (
                <ul {...listDragProps({ provided, snapshot })}>
                  {fieldNodes}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="ph3 pt2">
            <SlimCTA
              subheader="Add a field to this form group."
              customClasses={{
                container:
                  "w-100 flex flex-column flex-row-ns justify-between ba br2 hairline-1-dashed pa3 items-start items-center-ns bg-white",
                header: "f5 fw6",
                subheader: "f6 lh-copy"
              }}
              actions={[
                {
                  copy: "Add",
                  tag: BUTTON_TAGS.BUTTON,
                  theme: UI_THEME.SLIM,
                  classes: {
                    contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
                  },
                  onClick: () => {
                    setIntent(FIELD_GROUP_INTENT.ADD_FIELD);
                  }
                }
              ]}
            />
          </div>
        )}
      </Fragment>
    );
  } else {
    console.error(`No field group for active index ${activeIndex}`);
    content = (
      <div className="f5">No field group. Head back and select another</div>
    );
  }

  return content;
};

export default FieldGroupShow;
